import { dereferenceJSON } from "@/utils/formatters";
import type { JsonSchema, TransformResult, TransformOptions } from "./types";

/**
 * Step 1: Dereference $ref pointers in the schema
 *
 * This resolves all `$ref` pointers to their definitions, making the schema
 * fully expanded. Also preserves sibling properties that coexist with $ref.
 *
 * @param schema - Raw schema with $ref pointers
 * @param options - Transform options
 * @returns Promise resolving to transformed schema and metadata
 *
 * @example
 * const input = { $ref: "#/definitions/User", title: "User" };
 * const result = await dereferenceSchema(input);
 * // result.schema has User definition inline, with title preserved
 */
export async function dereferenceSchema(
  schema: JsonSchema,
  options?: TransformOptions
): Promise<TransformResult> {
  const { debug = false } = options || {};

  if (debug) {
    console.log("🔧 [dereferenceSchema] Starting...");
  }

  try {
    const derefResult = await dereferenceJSON(schema);

    if (!derefResult.data) {
      const msg = derefResult.message || "Failed to dereference schema";
      throw new Error(msg);
    }

    if (debug) {
      console.log("✅ [dereferenceSchema] Complete");
    }

    return {
      schema: derefResult.data,
      metadata: {
        name: "dereferenceSchema",
        transformedKeys: [], // dereferenceJSON handles this internally
        flags: {},
        skipped: false,
      },
    };
  } catch (err) {
    if (debug) {
      console.error("❌ [dereferenceSchema] Failed:", err);
    }
    throw err;
  }
}
